import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  Truck, 
  Package, 
  Plane, 
  Clock, 
  Shield, 
  Globe, 
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const HomePage = () => {
  const [trackingId, setTrackingId] = useState("");

  const handleTrack = (e) => {
    e.preventDefault();
    if (trackingId.trim()) {
      window.location.href = `/tracking?id=${encodeURIComponent(trackingId.trim())}`;
    }
  };

  const services = [
    {
      icon: Truck,
      title: "Load Transport",
      description: "Full truckload and LTL shipping solutions for your cargo needs.",
      image: "https://images.unsplash.com/photo-1632517306067-b54ab4d1f98d?w=600&q=80"
    },
    {
      icon: Package,
      title: "Parcel Delivery",
      description: "Fast and secure parcel delivery to any destination worldwide.",
      image: "https://images.unsplash.com/photo-1769477145932-edd5a9d7567c?w=600&q=80"
    },
    {
      icon: Plane,
      title: "Express Shipping",
      description: "Time-critical deliveries with guaranteed speed and reliability.",
      image: "https://images.unsplash.com/photo-1766793110924-98e05b48eadc?w=600&q=80"
    }
  ];

  const stats = [
    { value: "50K+", label: "Deliveries Monthly" },
    { value: "120+", label: "Countries Served" },
    { value: "500+", label: "Fleet Vehicles" },
    { value: "99.5%", label: "On-Time Delivery" }
  ];

  const features = [
    { icon: Clock, title: "24/7 Support", desc: "Round the clock customer service" },
    { icon: Shield, title: "Secure Handling", desc: "Your cargo is always protected" },
    { icon: Globe, title: "Global Network", desc: "Reach every corner of the world" }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1741482528360-662f28391474?w=1920&q=85')`
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-1.5 bg-blue-600/20 text-blue-300 text-sm font-medium rounded-full mb-6 uppercase tracking-wider">
              Global Logistics Partner
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 tracking-tight uppercase"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Ship Faster.<br />
              <span className="text-blue-400">Deliver Smarter.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10">
              Reliable logistics solutions for businesses of all sizes. From express parcels to full load transport, we deliver excellence worldwide.
            </p>
          </motion.div>

          {/* Quick Track Form */}
          <motion.form 
            onSubmit={handleTrack}
            className="max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="flex flex-col sm:flex-row gap-3 p-2 bg-white/10 backdrop-blur-lg rounded-sm border border-white/20">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Enter your tracking ID (e.g., SWL-2024-001)"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  className="pl-12 h-12 bg-white border-0 rounded-sm text-slate-900 placeholder:text-slate-500"
                  data-testid="hero-track-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-12 px-8 bg-blue-600 hover:bg-blue-700 rounded-sm font-semibold"
                data-testid="hero-track-button"
              >
                Track Now
              </Button>
            </div>
          </motion.form>

          {/* Demo tracking IDs */}
          <motion.p 
            className="text-slate-400 text-sm mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Try: SWL-2024-001, SWL-2024-002, or SWL-2024-003
          </motion.p>
        </div>
      </section>

      {/* Stats Band */}
      <section className="bg-slate-900 py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">Our Services</span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Comprehensive Logistics Solutions
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="service-card bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {service.description}
                  </p>
                  <Link 
                    to="/services" 
                    className="inline-flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors"
                  >
                    Learn More <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">Why Choose Us</span>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6 tracking-tight"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Trusted by Businesses Worldwide
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                With decades of experience in global logistics, we provide seamless shipping solutions that keep your business moving. Our advanced tracking systems and dedicated support ensure your cargo arrives safely and on time.
              </p>
              
              <div className="space-y-4 mb-8">
                {features.map((feature) => (
                  <div key={feature.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{feature.title}</h4>
                      <p className="text-sm text-slate-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/about">
                <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="learn-more-btn">
                  Learn More About Us <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1576888841486-afe7f1b2d74a?w=800&q=80"
                alt="Global shipping network"
                className="w-full rounded-sm shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-sm shadow-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <div className="text-2xl font-bold text-slate-900">99.5%</div>
                    <div className="text-sm text-slate-600">Satisfaction Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-50" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Ready to Ship?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Get started with SwiftLogix today and experience logistics done right. Contact our team for a personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="rounded-sm bg-blue-600 hover:bg-blue-700 px-8 h-12" data-testid="get-quote-btn">
                Get a Quote
              </Button>
            </Link>
            <Link to="/tracking">
              <Button variant="outline" className="rounded-sm border-slate-300 px-8 h-12" data-testid="track-shipment-btn">
                Track a Shipment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
